import { Column, CurrencyType, Row, Separator, useFormatCurrency } from "@dgs/core";
import { FC } from "react";
import { useTranslation } from "react-i18next";
import { RegistrationShopSummaryResource, ShopSummaryTaxResource } from "../../../shared/types";
import { Small, Subheading } from "./ShopSummaryComponents";

interface Props {
	currencyType: CurrencyType;
	prices: RegistrationShopSummaryResource;
	tobePaidTaxes: ShopSummaryTaxResource[];
	totalTaxes: ShopSummaryTaxResource[];
}

export function TotalPrices({
	currencyType,
	tobePaidTaxes,
	totalTaxes,
	prices: { alreadyPaidTotalAmount, payLaterTotalAmount, payNowTotalAmount, toBeRefundedAmount, totalAmount },
}: Props) {
	const { t } = useTranslation();
	const formatCurrency = useFormatCurrency(currencyType);

	return (
		<Column>
			<Column $gap={0}>
				<Row $justifyContent="space-between">
					<Subheading>{t("Total price")}:</Subheading>
					<Subheading>{formatCurrency(totalAmount)}</Subheading>
				</Row>
				{totalTaxes.map((tax) => (
					<TaxRow key={tax.percent} tax={tax} currencyType={currencyType} />
				))}
			</Column>
			<Column $gap={0}>
				<Row $justifyContent="space-between">
					<span>{t("Already paid:")}</span>
					<span>{formatCurrency(alreadyPaidTotalAmount)}</span>
				</Row>
				<Row $justifyContent="space-between">
					<span>{t("Pay later:")}</span>
					<span>{formatCurrency(payLaterTotalAmount)}</span>
				</Row>
			</Column>
			<Separator />
			<Column $gap={0}>
				<Row $justifyContent="space-between">
					<Subheading>{t("Pay now:")}</Subheading>
					<Subheading>{formatCurrency(payNowTotalAmount)}</Subheading>
				</Row>
				{toBeRefundedAmount > 0 && (
					<>
						<Row $justifyContent="space-between">
							<span>{t("To be refunded:")}</span>
							<span>{formatCurrency(toBeRefundedAmount)}</span>
						</Row>
						<Small>{t("Transactions cannot be offset. The refund will be processed separately.")}</Small>
					</>
				)}
			</Column>
			<Column $gap={0}>
				{tobePaidTaxes.map((tax) => (
					<TaxRow key={tax.percent} tax={tax} currencyType={currencyType} />
				))}
			</Column>
		</Column>
	);
}

type TaxRowProps = {
	tax: ShopSummaryTaxResource;
	currencyType: CurrencyType;
};

const TaxRow: FC<TaxRowProps> = ({ tax, currencyType }) => {
	const { t } = useTranslation();
	const formatCurrency = useFormatCurrency(currencyType);

	return (
		<Row $justifyContent="space-between">
			<span>{t("incl. {{percent}}% VAT:", { percent: tax.percent })}</span>
			<span>{formatCurrency(tax.amount)}</span>
		</Row>
	);
};
